import { Component } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { LanguageToggleComponent } from './shared/components/language-toggle/language-toggle.component';

/** localStorage key for the citizen's chosen UI language. */
export const LANG_STORAGE_KEY = 'imc.uiLang';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, LanguageToggleComponent, TranslatePipe],
  template: `
    <header class="app-header">
      <div class="container header-content">
        <div class="header-logo">
          <h1 class="header-title">{{ 'APP_TITLE' | translate }}</h1>
          <p class="header-subtitle">{{ 'FORM_TITLE' | translate }}</p>
        </div>
        <app-language-toggle />
      </div>
    </header>
    <main class="container main-content">
      <router-outlet />
    </main>
  `,
  styles: [`
    .app-header {
      background: linear-gradient(135deg, #1565c0, #0d47a1);
      color: white;
      padding: 12px 0;
      position: sticky;
      top: 0;
      z-index: 1000;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }

    .header-content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
    }

    .header-logo {
      flex: 1;
      min-width: 0;
    }

    .header-title {
      font-size: 1rem;
      font-weight: 600;
      margin: 0;
      line-height: 1.4;
    }

    .header-subtitle {
      font-size: 0.75rem;
      margin: 4px 0 0;
      opacity: 0.85;
      line-height: 1.4;
    }

    .main-content {
      padding-bottom: 80px; /* Space for fixed nav on mobile */
    }

    @media (min-width: 768px) {
      .app-header {
        padding: 16px 0;
      }

      .header-title {
        font-size: 1.375rem;
      }

      .header-subtitle {
        font-size: 0.875rem;
      }

      .main-content {
        padding-bottom: 24px;
      }
    }
  `],
})
export class App {
  constructor(
    private translate: TranslateService,
    private titleService: Title
  ) {
    translate.addLangs(['hi', 'en']);
    translate.setDefaultLang('hi');

    // Persist the citizen's last language across reloads. Without this the
    // app reverted to Hindi on every boot, which made hard-refreshing on
    // English drop the selection silently — and any keys missing from the
    // cached en.json fell back to Hindi (per setDefaultLang above).
    const saved = (typeof localStorage !== 'undefined' && localStorage.getItem(LANG_STORAGE_KEY)) || null;
    const initialLang = saved === 'en' || saved === 'hi' ? saved : 'hi';
    translate.use(initialLang);

    // Browser tab title — was hardcoded Devanagari in index.html. Keep it in
    // step with the active language so the tab matches the UI. translate.get
    // resolves once the translation map is loaded for the requested lang.
    this.applyTitle();
    translate.onLangChange.subscribe(() => this.applyTitle());
  }

  private applyTitle(): void {
    this.translate.get(['APP_TITLE', 'FORM_TITLE']).subscribe((t) => {
      this.titleService.setTitle(`${t['FORM_TITLE']} — ${t['APP_TITLE']}`);
    });
  }
}
